/** @jsx jsx */
import { array, string } from 'prop-types'
import { useContext } from 'react'
import { Flex, Box, Text, Image, jsx } from 'theme-ui'
import { QuestionStepContext } from '../../../context/QuestionStepContext'
import { useDeliverables } from '../../../hooks/useDeliverables'
import ReactMarkdown from 'react-markdown'
import RadioInput from '../../common/form/FormInput/RadioInput'
import { useFormikContext } from 'formik'
import { IsQuestionRequiredError } from '../../common/form/InputValidationHelper'

const AudienceMatrix = ({ questions }) => {
  const { template, nextButtonClicked } = useContext(QuestionStepContext)
  const { sections, currentStep } = template
  const { deliverables } = useDeliverables()
  const { values } = useFormikContext()

  return (
    <Flex
      as="section"
      sx={{
        flexDirection: 'column',
        paddingBottom: '70px',
        mx: [3, 32],
      }}
    >
      {deliverables.length > 0 && (
        <Box
          sx={{
            gap: '25px',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            marginTop: '30px',
            marginBottom: '50px',
          }}
        >
          {deliverables.map(
            ({ image, name, descriptionMarketingStrategies }, index) => {
              return (
                <Flex
                  key={index}
                  sx={{
                    boxShadow: '0px 2px 5px 0px #00000040',
                    padding: '12px',
                    alignItems: 'flex-start',
                    gap: '16px',
                  }}
                >
                  <Image
                    sx={{
                      width: '65px',
                      height: '65px',
                      objectFit: 'cover',
                      borderRadius: '50%',
                      border: '1px solid',
                      borderColor: 'forestDisabled',
                    }}
                    src={image}
                    alt={name}
                  />
                  <Flex
                    sx={{
                      flexDirection: 'column',
                      gap: '4px',
                      flex: 1,
                    }}
                  >
                    <Text
                      sx={{
                        fontWeight: 600,
                        fontSize: '15px',
                        color: 'forest',
                      }}
                    >
                      {name}
                    </Text>
                    <Text
                      sx={{
                        color: 'darkGray',
                        fontSize: '15px',
                        p: {
                          margin: 0,
                        },
                        li: {
                          margin: 0,
                        },
                      }}
                    >
                      <ReactMarkdown>
                        {descriptionMarketingStrategies}
                      </ReactMarkdown>
                    </Text>
                  </Flex>
                </Flex>
              )
            }
          )}
        </Box>
      )}
      <Text
        sx={{
          fontSize: '1.5625rem',
        }}
      >
        {sections[currentStep]?.subTitle}
      </Text>
      <Text variant="mediumText" sx={{ color: 'muted', my: 4 }}>
        Please select one option in each row.
      </Text>
      <Box
        as="table"
        sx={{
          marginTop: '20px',
        }}
      >
        <thead>
          <tr>
            <th />
            {questions[0].answers.map(answer => (
              <th key={answer.id}>
                <Text
                  as="span"
                  sx={{
                    fontWeight: 400,
                    fontSize: '1rem',
                    textAlign: 'center',
                    color: 'darkGray',
                  }}
                >
                  {answer.text}
                </Text>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {questions.map(question => {
            return (
              <Box
                as="tr"
                sx={{
                  '&:last-child': {
                    td: {
                      border: 'none',
                    },
                  },
                }}
                key={question.id}
              >
                <td>
                  <Text
                    as="span"
                    sx={{
                      fontWeight: 400,
                      fontSize: '1rem',
                      textAlign: 'center',
                      color: 'darkGray',
                    }}
                  >
                    {question.text}
                  </Text>
                </td>
                {question.answers.map(answer => (
                  <Box
                    as="td"
                    sx={{
                      position: 'relative',
                      '.radio-input': {
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        marginRight: '-50%',
                        transform: 'translate(-50%, -50%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        span: {
                          top: '50%',
                          left: '50%',
                          marginRight: '-50%',
                          transform: 'translate(-50%, -50%)',
                        },
                      },
                    }}
                    key={answer.id}
                  >
                    <RadioInput
                      values={values}
                      question={question}
                      option={answer}
                      hideLabel
                      sx={{
                        '> .radio-input span': {
                          border:
                            IsQuestionRequiredError(question, values, false) &&
                            nextButtonClicked &&
                            '2px solid red',
                        },
                      }}
                    />
                  </Box>
                ))}
              </Box>
            )
          })}
        </tbody>
      </Box>
    </Flex>
  )
}

AudienceMatrix.propTypes = {
  type: string,
  questions: array.isRequired,
}

export default AudienceMatrix
